import { cn } from "@/shared/lib/utils";
import type { MatrixColumnDef, MatrixPreviewRowUi } from "@/domains/matrix";

const CUT_OFF_MASK = "linear-gradient(to bottom, black 15%, transparent)";

type MatrixPreviewProps = {
  columns: MatrixColumnDef[];
  rows: MatrixPreviewRowUi[];
  rowCount: number;
  isActive?: boolean;
  className?: string;
};

// The head of the grid on a rail card: the first column, plus a count of the rest.
export function MatrixPreview({
  columns,
  rows,
  rowCount,
  isActive = false,
  className,
}: MatrixPreviewProps) {
  const [firstColumn, ...restColumns] = columns;
  if (!firstColumn && rows.length === 0) return null;

  const hasMoreRows = rowCount > rows.length;
  const hasMoreColumns = restColumns.length > 0;
  const innerLine = isActive ? "border-primary/20" : "border-border/60";

  return (
    <span className={cn("flex min-w-0 flex-col", className)}>
      <span
        className={cn(
          "text-foreground/80 flex flex-col divide-y overflow-hidden rounded-md border text-[11px] leading-none font-normal",
          isActive
            ? "border-primary/20 divide-primary/20"
            : "border-border/70 divide-border/60",
          hasMoreRows && "rounded-b-none border-b-0",
        )}
      >
        {firstColumn ? (
          <span
            className={cn(
              "text-muted-foreground flex",
              isActive ? "bg-primary/10" : "bg-muted/70",
            )}
          >
            <span className="min-w-0 flex-1 truncate px-1.5 py-1">
              {firstColumn.label}
            </span>
            {hasMoreColumns ? (
              <span
                className={cn(
                  "w-7 shrink-0 border-l px-1 py-1 text-center tabular-nums",
                  innerLine,
                )}
              >
                +{restColumns.length}
              </span>
            ) : null}
          </span>
        ) : null}

        {rows.map((row) => (
          <span key={row.analysisId} className="bg-background flex">
            <span className="min-w-0 flex-1 truncate px-1.5 py-1">
              {row.value || <span className="text-muted-foreground">-</span>}
            </span>
            {hasMoreColumns ? (
              <span className={cn("w-7 shrink-0 border-l", innerLine)} />
            ) : null}
          </span>
        ))}
      </span>

      {hasMoreRows ? (
        <span
          aria-hidden
          className={cn(
            "bg-background flex h-3 shrink-0 border-x border-t",
            isActive ? "border-x-primary/20" : "border-x-border/70",
            isActive ? "border-t-primary/20" : "border-t-border/60",
          )}
          style={{ maskImage: CUT_OFF_MASK, WebkitMaskImage: CUT_OFF_MASK }}
        >
          <span className="min-w-0 flex-1" />
          {hasMoreColumns ? (
            <span className={cn("w-7 shrink-0 border-l", innerLine)} />
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
